import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Clock, EnvelopeSimple, Phone, Shield, User as UserIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";

interface UserProfileProps {
  userId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

interface UserDetails {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  avatar: string;
  phone?: string;
  is_active: number;
  created_at?: string;
  last_login?: string;
}

export function UserProfile({ userId, isOpen, onClose }: UserProfileProps) {
  const [user, setUser] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userId && isOpen) {
      // In a real app, fetch from API
      // For now, we'll use mock data
      setLoading(true);
      // Simulate API call
      setTimeout(() => {
        setUser({
          id: userId,
          name: "John Doe",
          email: "john@example.com",
          role: "employee",
          department: "Engineering",
          avatar: "👨‍💻",
          phone: "+1 (555) 123-4567",
          is_active: 1,
          created_at: new Date("2024-01-15").toISOString(),
          last_login: new Date().toISOString()
        });
        setLoading(false);
      }, 500);
    }
  }, [userId, isOpen]);

  const getRoleBadge = (role: string) => {
    const colors = {
      superadmin: "bg-purple-600 text-white",
      admin: "bg-purple-100 text-purple-700",
      hr: "bg-orange-100 text-orange-700",
      manager: "bg-blue-100 text-blue-700",
      employee: "bg-green-100 text-green-700"
    };
    return colors[role as keyof typeof colors] || colors.employee;
  };

  if (!user && !loading) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>User Profile</DialogTitle>
          <DialogDescription>
            Detailed information about the user account
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : user ? (
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="permissions">Permissions</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              {/* Profile Header */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <Avatar className="h-20 w-20">
                      <AvatarImage src={user.avatar?.startsWith('http') ? user.avatar : undefined} />
                      <AvatarFallback className="text-2xl">{user.avatar || user.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold">{user.name}</h3>
                      <p className="text-slate-500 mb-2">{user.email}</p>
                      <div className="flex gap-2">
                        <Badge className={getRoleBadge(user.role)}>
                          <Shield className="w-3 h-3 mr-1" weight="fill" />
                          {user.role === 'superadmin' ? 'Super Admin' : user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </Badge>
                        <Badge variant={user.is_active === 1 ? "default" : "secondary"}>
                          {user.is_active === 1 ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3">
                    <EnvelopeSimple className="w-5 h-5 text-slate-400" weight="duotone" />
                    <div>
                      <Label className="text-xs text-slate-500">Email</Label>
                      <p className="font-medium">{user.email}</p>
                    </div>
                  </div>
                  {user.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-slate-400" weight="duotone" />
                      <div>
                        <Label className="text-xs text-slate-500">Phone</Label>
                        <p className="font-medium">{user.phone}</p>
                      </div>
                    </div>
                  )}
                  <Separator />
                  <div className="flex items-center gap-3">
                    <UserIcon className="w-5 h-5 text-slate-400" weight="duotone" />
                    <div>
                      <Label className="text-xs text-slate-500">Department</Label>
                      <p className="font-medium">{user.department}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Account Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Account Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-slate-400" weight="duotone" />
                    <div>
                      <Label className="text-xs text-slate-500">Joined Date</Label>
                      <p className="font-medium">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }) : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-slate-400" weight="duotone" />
                    <div>
                      <Label className="text-xs text-slate-500">Last Login</Label>
                      <p className="font-medium">
                        {user.last_login ? new Date(user.last_login).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'Never'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="activity" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Activity</CardTitle>
                  <CardDescription>Login history and recent actions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="text-sm text-slate-500">
                      Activity tracking coming soon...
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="permissions" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Role Permissions</CardTitle>
                  <CardDescription>Access rights and capabilities</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {user.role === 'superadmin' && (
                      <div className="text-sm">
                        <p className="font-medium mb-2">Super Admin has full system access:</p>
                        <ul className="list-disc list-inside space-y-1 text-slate-600">
                          <li>Manage all users including other admins</li>
                          <li>Access to all system settings</li>
                          <li>View and modify all data</li>
                          <li>System configuration and security settings</li>
                        </ul>
                      </div>
                    )}
                    {user.role === 'admin' && (
                      <div className="text-sm">
                        <p className="font-medium mb-2">Admin has management access:</p>
                        <ul className="list-disc list-inside space-y-1 text-slate-600">
                          <li>Manage employees and managers</li>
                          <li>Approve leave requests</li>
                          <li>View attendance and timesheets</li>
                          <li>Generate reports</li>
                        </ul>
                      </div>
                    )}
                    {user.role === 'hr' && (
                      <div className="text-sm">
                        <p className="font-medium mb-2">HR has user management access:</p>
                        <ul className="list-disc list-inside space-y-1 text-slate-600">
                          <li>Create and manage user accounts</li>
                          <li>Approve leave requests</li>
                          <li>View employee records</li>
                          <li>Manage employee onboarding</li>
                        </ul>
                      </div>
                    )}
                    {user.role === 'manager' && (
                      <div className="text-sm">
                        <p className="font-medium mb-2">Manager has team oversight:</p>
                        <ul className="list-disc list-inside space-y-1 text-slate-600">
                          <li>View team attendance</li>
                          <li>Approve team leave requests</li>
                          <li>Review team timesheets</li>
                          <li>Access team reports</li>
                        </ul>
                      </div>
                    )}
                    {user.role === 'employee' && (
                      <div className="text-sm">
                        <p className="font-medium mb-2">Employee has standard access:</p>
                        <ul className="list-disc list-inside space-y-1 text-slate-600">
                          <li>Mark attendance</li>
                          <li>Request leave</li>
                          <li>Log timesheet entries</li>
                          <li>View personal dashboard</li>
                        </ul>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : null}

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
